if (!customElements.get('product-comparison')) {
  class ProductComparison extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;

      const node = this.querySelector('[data-product-comparison]');
      if (!node) return;

      const payload = ProductComparison.parseJson(node.textContent, {});
      this.storeDomain = (this.dataset.storeDomain || '').trim();
      this.storefrontToken = (this.dataset.storefrontToken || '').trim();
      this.collectionHandle = (this.dataset.collectionHandle || '').trim();
      this.queryParam = payload.queryParam || 'ids';
      this.maxSlots = Number(payload.maxSlots) || 4;
      this.specRows = ProductComparison.parseRows(payload.specRows);
      this.specMetafieldRefs = ProductComparison.specMetafieldRefs(this.specRows);
      this.maxProducts = ProductComparison.normalizeLimit(payload.maxProducts);
      this.fetchPageSize = ProductComparison.normalizePageSize(payload.fetchPageSize);
      this.productSocKey = (payload.productSocKey || 'soc').trim();
      this.emptySlotImage = String(payload.emptySlotImage || '').trim();

      this.defaultHandles = [];
      this.unavailableUrlHandles = [];
      this.productMap = new Map();
      this.chipOptions = [];
      this.chipCatalog = new Map();
      this.productIndex = [];
      this.productIndexLoaded = false;
      this.productIndexPromise = null;
      this.slotProducts = Array.from({ length: this.maxSlots }, () => null);
      this.slotChip = Array.from({ length: this.maxSlots }, () => '');

      this.head = this.querySelector('[data-comparison-head]');
      this.body = this.querySelector('[data-comparison-body]');
      this.hideSame = this.querySelector('[data-hide-same]');
      this.highlightDiff = this.querySelector('[data-highlight-diff]');
      this.statusNode = this.querySelector('[data-comparison-status]');
      this.loadingTextNode = this.querySelector('[data-comparison-loading-text]');

      this.bindEvents();
      this.bindGlobalEvents();
      this.render();
      this.loadInitialData();
    }

    bindEvents() {
      this.addEventListener('change', (event) => {
        const target = event.target;
        const action = target?.dataset?.action;
        const slot = Number(target?.dataset?.slot);
        if (Number.isNaN(slot)) return;

        if (action === 'select-chip') {
          this.handleChipChange(slot, target.value || '');
          return;
        }

        if (action === 'select-product') {
          this.selectProduct(slot, target.value || '');
        }
      });

      this.addEventListener('input', async (event) => {
        const search = event.target.closest('[data-comparison-dropdown-search]');
        if (!search) return;

        const dropdown = search.closest('[data-comparison-dropdown]');
        if (!dropdown || dropdown.classList.contains('is-disabled')) return;
        if (await this.loadChipOptionsForDropdown(dropdown)) return;
        this.openDropdown(dropdown, true);
        this.filterDropdownOptions(search);
      });

      this.addEventListener('focusin', async (event) => {
        const search = event.target.closest('[data-comparison-dropdown-search]');
        if (!search) return;

        const dropdown = search.closest('[data-comparison-dropdown]');
        if (!dropdown || dropdown.classList.contains('is-disabled')) return;
        if (await this.loadChipOptionsForDropdown(dropdown)) return;
        this.openDropdown(dropdown);
        search.select();
      });

      this.addEventListener('keydown', (event) => {
        const search = event.target.closest('[data-comparison-dropdown-search]');
        if (!search) return;

        const dropdown = search.closest('[data-comparison-dropdown]');
        if (!dropdown) return;

        if (event.key === 'Escape') {
          this.closeDropdowns();
          search.blur();
          return;
        }

        if (event.key !== 'Enter') return;

        const option = [...dropdown.querySelectorAll('[data-comparison-dropdown-option]')].find((item) => !item.hidden);
        if (!option) return;

        event.preventDefault();
        this.chooseDropdownOption(option);
      });

      this.addEventListener('click', async (event) => {
        const dropdownToggle = event.target.closest('[data-comparison-dropdown-toggle]');
        if (dropdownToggle) {
          const dropdown = dropdownToggle.closest('[data-comparison-dropdown]');
          if (!dropdown || dropdown.classList.contains('is-disabled')) return;
          if (await this.loadChipOptionsForDropdown(dropdown)) return;
          this.openDropdown(dropdown);
          dropdown.querySelector('[data-comparison-dropdown-search]')?.focus();
          return;
        }

        const dropdownOption = event.target.closest('[data-comparison-dropdown-option]');
        if (dropdownOption) {
          this.chooseDropdownOption(dropdownOption);
          return;
        }

        if (!event.target.closest('[data-comparison-dropdown]')) {
          this.closeDropdowns();
        }

        const button = event.target.closest('[data-action]');
        if (!button) return;

        const action = button.dataset.action;
        const slot = Number(button.dataset.slot);
        if (Number.isNaN(slot)) return;

        if (action === 'remove-product') {
          this.slotProducts[slot] = null;
          this.render();
          this.syncUrl();
          return;
        }

        if (action === 'move-left' && slot > 0) {
          this.swapSlots(slot, slot - 1);
          return;
        }

        if (action === 'move-right' && slot < this.maxSlots - 1) {
          this.swapSlots(slot, slot + 1);
        }
      });

      this.hideSame?.addEventListener('change', () => this.render());
      this.highlightDiff?.addEventListener('change', () => this.render());
    }

    bindGlobalEvents() {
      document.addEventListener('click', (event) => {
        if (this.contains(event.target)) return;
        this.closeDropdowns();
      });
    }

    openDropdown(dropdown, shouldFilter = false) {
      if (!dropdown) return;
      this.closeDropdowns(dropdown);
      dropdown.classList.add('is-open');
      dropdown.querySelector('[data-comparison-dropdown-toggle]')?.setAttribute('aria-expanded', 'true');
      const search = dropdown.querySelector('[data-comparison-dropdown-search]');
      if (search && shouldFilter) {
        this.filterDropdownOptions(search);
      } else {
        dropdown.querySelectorAll('[data-comparison-dropdown-option]').forEach((option) => {
          option.hidden = false;
        });
      }
    }

    closeDropdowns(except = null) {
      this.querySelectorAll('[data-comparison-dropdown].is-open').forEach((dropdown) => {
        if (dropdown === except) return;
        dropdown.classList.remove('is-open');
        dropdown.querySelector('[data-comparison-dropdown-toggle]')?.setAttribute('aria-expanded', 'false');
      });
    }

    chooseDropdownOption(option) {
      const action = option.dataset.comparisonAction;
      const slot = Number(option.dataset.slot);
      const value = option.dataset.value || '';
      const dropdown = option.closest('[data-comparison-dropdown]');
      const search = dropdown?.querySelector('[data-comparison-dropdown-search]');

      if (search) {
        search.value = option.textContent.trim();
      }

      this.closeDropdowns();
      if (Number.isNaN(slot)) return;

      if (action === 'select-chip') {
        this.handleChipChange(slot, value);
        return;
      }

      if (action === 'select-product') {
        this.selectProduct(slot, value);
      }
    }

    filterDropdownOptions(search) {
      const dropdown = search.closest('[data-comparison-dropdown]');
      if (!dropdown) return;

      const term = ProductComparison.normalizeComparable(search.value);
      dropdown.querySelectorAll('[data-comparison-dropdown-option]').forEach((option) => {
        const label = ProductComparison.normalizeComparable(option.textContent);
        option.hidden = Boolean(term) && !label.includes(term);
      });
    }

    async loadInitialData() {
      if (!this.storeDomain || !this.storefrontToken || !this.collectionHandle || !this.productSocKey) {
        this.setStatus('Storefront API config is incomplete.');
        return;
      }

      try {
        if (this.readHandlesFromUrl().length > 0) {
          this.setStatus('Loading selected products...');
        }

        await this.hydrateByUrl();
        this.hydrateByDefault();

        if (this.unavailableUrlHandles.length > 0) {
          const missing = this.unavailableUrlHandles.slice(0, 4).join(', ');
          const suffix = this.unavailableUrlHandles.length > 4 ? '...' : '';
          this.setStatus(`Some compared products are unavailable: ${missing}${suffix}`);
        } else {
          this.setStatus('');
        }

        this.render();
      } catch (error) {
        this.setStatus(`Failed to load comparison data: ${String(error?.message || 'Unknown error')}`);
        this.render();
      }
    }

    async loadChipOptionsForDropdown(dropdown) {
      if (dropdown?.dataset?.comparisonAction !== 'select-chip' || this.productIndexLoaded) return false;

      const slot = dropdown.dataset.slot;
      this.closeDropdowns();
      this.setStatus('Loading product index...');

      try {
        await this.ensureChipOptions();
        this.render();

        if (this.chipOptions.length === 0) {
          this.setStatus('No comparable cores were found.');
          return true;
        }

        this.setStatus('');
        const nextDropdown = this.querySelector(`[data-comparison-dropdown][data-comparison-action="select-chip"][data-slot="${ProductComparison.escapeSelector(slot)}"]`);
        this.openDropdown(nextDropdown);
        nextDropdown?.querySelector('[data-comparison-dropdown-search]')?.focus();
      } catch (error) {
        this.setStatus(`Failed to load cores: ${String(error?.message || 'Unknown error')}`);
      }

      return true;
    }

    async ensureChipOptions() {
      await this.fetchAllProductIndex();
      this.chipCatalog = new Map();

      this.chipOptions.forEach((chip) => {
        const entry = {
          chip,
          products: [],
          loading: false,
          loaded: false,
          error: '',
          promise: null,
        };

        [chip.id, chip.handle, chip.name].forEach((value) => {
          const key = ProductComparison.normalizeComparable(value);
          if (key) this.chipCatalog.set(key, entry);
        });
      });

      return this.chipOptions;
    }

    async hydrateByUrl() {
      const handles = this.readHandlesFromUrl();
      if (handles.length === 0) return;

      const products = await this.fetchProductsByHandles(handles);
      const found = new Map(products.map((product) => [product.handle, product]));

      handles.forEach((handle, index) => {
        const product = found.get(handle);
        if (!product) {
          this.unavailableUrlHandles.push(handle);
          return;
        }

        this.productMap.set(product.handle, product);
        this.slotProducts[index] = product;
        this.slotChip[index] = ProductComparison.productChipValue(product);
      });
    }

    hydrateByDefault() {
      if (this.slotProducts.some(Boolean)) return;

      this.defaultHandles.slice(0, this.maxSlots).forEach((handle, index) => {
        const normalizedHandle = ProductComparison.normalizeHandle(handle);
        const product = this.productMap.get(normalizedHandle);
        if (!product) return;
        this.slotProducts[index] = product;
        this.slotChip[index] = ProductComparison.productChipValue(product);
      });
    }

    readHandlesFromUrl() {
      const params = new URLSearchParams(window.location.search);
      const raw = params.get(this.queryParam);
      if (!raw) return [];

      return raw
        .split(',')
        .map((handle) => ProductComparison.normalizeHandle(handle))
        .filter(Boolean)
        .slice(0, this.maxSlots);
    }

    async fetchProductsByHandles(handles) {
      const normalizedHandles = [...new Set((handles || []).map((handle) => ProductComparison.normalizeHandle(handle)).filter(Boolean))];
      if (normalizedHandles.length === 0) return [];

      const byHandle = new Map();
      const results = await Promise.all(normalizedHandles.map((handle) =>
        this.storefrontRequest(ProductComparison.productByHandleQuery(this.productSocKey, this.specMetafieldRefs), { handle })
      ));

      results.forEach((payload) => {
        const product = ProductComparison.productFromNode(payload?.data?.product, this.specMetafieldRefs);
        if (!product) return;
        byHandle.set(product.handle, this.normalizeProduct(product));
      });

      return normalizedHandles.map((handle) => byHandle.get(handle)).filter(Boolean);
    }

    async handleChipChange(slot, chipName) {
      const normalizedChip = String(chipName || '').trim();
      const entry = this.getChipEntry(normalizedChip);
      const chip = entry?.chip || { id: normalizedChip, name: normalizedChip };
      const chipLabel = chip.name || normalizedChip;
      const selectedProduct = this.slotProducts[slot];

      this.slotChip[slot] = normalizedChip;
      if (selectedProduct && !ProductComparison.productMatchesChip(selectedProduct, chip)) {
        this.slotProducts[slot] = null;
      }

      this.render();
      this.syncUrl();

      if (!normalizedChip) {
        this.setStatus('');
        return;
      }

      this.setStatus(`Loading ${chipLabel} products...`);

      try {
        await this.ensureChipProducts(normalizedChip);
        if (entry && entry.products.length === 0) {
          this.setStatus(`No products found for ${chipLabel}.`);
        } else {
          this.setStatus('');
        }
      } catch (error) {
        this.setStatus(`Failed to load ${chipLabel} products: ${String(error?.message || 'Unknown error')}`);
      }

      this.render();
    }

    getChipEntry(chipName) {
      return this.chipCatalog.get(ProductComparison.normalizeComparable(chipName)) || null;
    }

    async ensureChipProducts(chipName) {
      const entry = this.getChipEntry(chipName);
      if (!entry) return [];
      if (entry.loaded) return entry.products;
      if (entry.promise) return entry.promise;

      entry.loading = true;
      entry.error = '';
      entry.promise = this.fetchAllProductsForChip(chipName)
        .then((products) => {
          entry.products = products;
          entry.loaded = true;
          entry.loading = false;
          entry.promise = null;
          products.forEach((product) => {
            this.productMap.set(product.handle, product);
          });
          return products;
        })
        .catch((error) => {
          entry.loading = false;
          entry.error = String(error?.message || 'Unknown error');
          entry.promise = null;
          throw error;
        });

      this.render();
      return entry.promise;
    }

    async fetchAllProductsForChip(chipName) {
      const entry = this.getChipEntry(chipName);
      const chip = entry?.chip || { id: String(chipName || '').trim(), name: String(chipName || '').trim() };
      const products = await this.fetchAllProductIndex();
      return products.filter((product) => ProductComparison.productMatchesChip(product, chip));
    }

    async fetchAllProductIndex() {
      if (this.productIndexLoaded) return this.productIndex;
      if (this.productIndexPromise) return this.productIndexPromise;

      this.productIndexPromise = this.loadProductIndex()
        .then((products) => {
          this.productIndex = products;
          this.chipOptions = ProductComparison.chipsFromProducts(products);
          this.productIndexLoaded = true;
          this.productIndexPromise = null;
          products.forEach((product) => {
            this.productMap.set(product.handle, product);
          });
          return products;
        })
        .catch((error) => {
          this.productIndexPromise = null;
          throw error;
        });

      return this.productIndexPromise;
    }

    async loadProductIndex() {
      const products = [];
      let hasNextPage = true;
      let after = null;
      let dynamicPageSize = this.fetchPageSize;
      let retryCount = 0;
      let scanned = 0;

      while (hasNextPage && scanned < this.maxProducts) {
        const remaining = this.maxProducts - scanned;
        const first = Number.isFinite(remaining) ? Math.max(1, Math.min(dynamicPageSize, remaining)) : dynamicPageSize;

        let payload;
        try {
          payload = await this.storefrontRequest(ProductComparison.productIndexQuery(this.productSocKey), {
            handle: this.collectionHandle,
            first,
            after,
          });
          retryCount = 0;
        } catch (error) {
          const message = String(error?.message || '');
          if (/Internal error/i.test(message) && dynamicPageSize > 50) {
            dynamicPageSize = Math.max(50, Math.floor(dynamicPageSize / 2));
            continue;
          }
          if (/Internal error/i.test(message) && retryCount < 2) {
            retryCount += 1;
            await ProductComparison.sleep(700 * retryCount);
            continue;
          }
          throw error;
        }

        const collection = payload?.data?.collection;
        if (!collection) {
          throw new Error('Collection was not found via Storefront API.');
        }

        const edges = collection.products?.edges || [];
        scanned += edges.length;
        edges.forEach((edge) => {
          const product = ProductComparison.productIndexFromNode(edge?.node);
          if (!product) return;
          products.push(this.normalizeProduct(product));
        });

        hasNextPage = Boolean(collection.products?.pageInfo?.hasNextPage);
        after = collection.products?.pageInfo?.endCursor || null;
      }

      return products;
    }

    async storefrontRequest(query, variables) {
      const response = await fetch(`https://${this.storeDomain}/api/2025-10/graphql.json`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': this.storefrontToken,
        },
        body: JSON.stringify({ query, variables }),
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const payload = await response.json();
      if (Array.isArray(payload?.errors) && payload.errors.length > 0) {
        const message = payload.errors.map((item) => item.message).filter(Boolean).join('; ') || 'GraphQL error';
        throw new Error(message);
      }

      return payload;
    }

    setStatus(message) {
      if (!this.statusNode) return;
      const text = String(message || '').trim();
      const isLoading = /^Loading\b/i.test(text);

      this.classList.toggle('is-loading', isLoading);
      this.statusNode.hidden = !text || isLoading;
      this.statusNode.textContent = text;

      if (this.loadingTextNode) {
        this.loadingTextNode.textContent = isLoading ? text : '';
      }
    }

    async selectProduct(slot, handle) {
      const normalizedHandle = ProductComparison.normalizeHandle(handle);
      if (!normalizedHandle) {
        this.slotProducts[slot] = null;
        this.render();
        this.syncUrl();
        return;
      }

      let selected = this.productMap.get(normalizedHandle);
      if (!selected) return;

      if (!selected.full) {
        try {
          this.setStatus(`Loading ${selected.title || normalizedHandle} details...`);
          const products = await this.fetchProductsByHandles([normalizedHandle]);
          selected = products[0] || selected;
          this.productMap.set(normalizedHandle, selected);
          this.setStatus('');
        } catch (error) {
          this.setStatus(`Failed to load ${selected.title || normalizedHandle} details: ${String(error?.message || 'Unknown error')}`);
          return;
        }
      }

      const existingIndex = this.slotProducts.findIndex((item) => item && item.handle === normalizedHandle);
      if (existingIndex >= 0 && existingIndex !== slot) {
        this.slotProducts[existingIndex] = null;
      }

      this.slotProducts[slot] = selected;
      this.slotChip[slot] = ProductComparison.productChipValue(selected);
      this.render();
      this.syncUrl();
    }

    swapSlots(from, to) {
      const fromProduct = this.slotProducts[from];
      const fromChip = this.slotChip[from];
      this.slotProducts[from] = this.slotProducts[to];
      this.slotChip[from] = this.slotChip[to];
      this.slotProducts[to] = fromProduct;
      this.slotChip[to] = fromChip;
      this.render();
      this.syncUrl();
    }

    syncUrl() {
      const handles = this.slotProducts.filter(Boolean).map((item) => item.handle);
      const url = new URL(window.location.href);

      if (handles.length > 0) {
        url.searchParams.set(this.queryParam, handles.join(','));
      } else {
        url.searchParams.delete(this.queryParam);
      }

      window.history.replaceState({}, '', url.toString());
    }

    normalizeProduct(item) {
      return {
        id: item?.id || '',
        handle: ProductComparison.normalizeHandle(item?.handle),
        title: item?.title || '',
        url: item?.url || '#',
        image: item?.image || '',
        chipName: item?.chipName || '',
        chipRefs: Array.isArray(item?.chipRefs) ? item.chipRefs : [],
        full: Boolean(item?.full),
        specs: ProductComparison.normalizeSpecs(item?.specs),
      };
    }

    getProductsForChip(chipName) {
      const entry = this.getChipEntry(chipName);
      return entry?.products || [];
    }

    render() {
      if (!this.head || !this.body) return;
      this.renderHead();
      this.renderBody();
    }

    renderHead() {
      const sticky = `<th class="pcmp__sticky pcmp__label pcmp__compare-count">Compare ${this.maxSlots} Products</th>`;
      const columns = this.slotProducts.map((item, slot) => (item ? this.productColumn(item, slot) : this.emptyColumn(slot))).join('');
      this.head.innerHTML = `<tr>${sticky}${columns}</tr>`;
    }

    productColumn(product, slot) {
      const image = product.image
        ? `<div class="pcmp__thumb"><img src="${ProductComparison.escape(product.image)}" alt="${ProductComparison.escape(product.title)}"></div>`
        : '';
      const leftDisabled = slot === 0 ? ' disabled' : '';
      const rightDisabled = slot === this.maxSlots - 1 ? ' disabled' : '';

      return `<th class="pcmp__slot">
        <div class="pcmp__product">
          <button type="button" class="pcmp__remove" data-action="remove-product" data-slot="${slot}" aria-label="Remove product">&times;</button>
          ${image}
          <p class="pcmp__title" title="${ProductComparison.escape(product.title)}">${ProductComparison.escape(product.title)}</p>
          <div class="pcmp__actions">
            <button type="button" class="pcmp__move pcmp__move--left" data-action="move-left" data-slot="${slot}"${leftDisabled} aria-label="Move left"></button>
            <span>Move</span>
            <button type="button" class="pcmp__move pcmp__move--right" data-action="move-right" data-slot="${slot}"${rightDisabled} aria-label="Move right"></button>
          </div>
        </div>
      </th>`;
    }

    emptyColumn(slot) {
      const selectedChip = this.slotChip[slot] || '';

      const entry = this.getChipEntry(selectedChip);
      const products = selectedChip ? this.getProductsForChip(selectedChip) : [];

      let productPlaceholder = 'Search product';
      let productDisabled = true;

      if (!selectedChip) {
        productPlaceholder = 'Search product';
      } else if (entry?.loading) {
        productPlaceholder = 'Loading products...';
      } else if (entry?.error) {
        productPlaceholder = 'Failed to load products';
      } else if (products.length === 0) {
        productPlaceholder = 'No products found';
      } else {
        productDisabled = false;
      }

      const emptyImage = this.emptySlotImage
        ? `<img src="${ProductComparison.escape(this.emptySlotImage)}" alt="" loading="lazy">`
        : '';

      const chipDropdown = this.dropdownControl({
        action: 'select-chip',
        slot,
        placeholder: 'Search core',
        value: selectedChip,
        options: this.chipOptions.map((chip) => ({ value: chip.id || chip.name, label: chip.name })),
      });
      const productDropdown = this.dropdownControl({
        action: 'select-product',
        slot,
        placeholder: productPlaceholder,
        value: this.slotProducts[slot]?.handle || '',
        options: products.map((item) => ({ value: item.handle, label: item.title })),
        disabled: productDisabled,
      });

      return `<th class="pcmp__slot pcmp__slot--empty">
        <div class="pcmp__slot-empty">
          <div class="pcmp__empty-visual" aria-hidden="true">${emptyImage}</div>
          ${chipDropdown}
          ${productDropdown}
        </div>
      </th>`;
    }

    dropdownControl({ action, slot, placeholder, value = '', options = [], disabled = false }) {
      const selectedOption = options.find((option) =>
        ProductComparison.isSameComparable(option.value, value) ||
        ProductComparison.isSameComparable(option.label, value)
      );
      const label = selectedOption?.label || placeholder;
      const disabledClass = disabled ? ' is-disabled' : '';
      const actionClass = ` pcmp__dropdown--${ProductComparison.escape(action)}`;
      const searchLabel = action === 'select-chip' ? 'Search core' : 'Search product';
      const inputValue = selectedOption?.label || '';
      const items = options.length > 0
        ? options.map((option) => {
          const selected = ProductComparison.isSameComparable(option.value, value) ? ' is-selected' : '';
          return `<button type="button" class="pcmp__dropdown-option${selected}" title="${ProductComparison.escape(option.label)}" data-comparison-dropdown-option data-comparison-action="${ProductComparison.escape(action)}" data-slot="${slot}" data-value="${ProductComparison.escape(option.value)}">${ProductComparison.escape(option.label)}</button>`;
        }).join('')
        : `<p class="pcmp__dropdown-empty">${ProductComparison.escape(placeholder)}</p>`;

      return `
        <div class="pcmp__dropdown${actionClass}${disabledClass}" data-comparison-dropdown data-comparison-action="${ProductComparison.escape(action)}" data-slot="${slot}">
          <input class="pcmp__dropdown-search" type="search" placeholder="${ProductComparison.escape(label || searchLabel)}" value="${ProductComparison.escape(inputValue)}" title="${ProductComparison.escape(label)}" data-comparison-dropdown-toggle data-comparison-dropdown-search data-comparison-action="${ProductComparison.escape(action)}" data-slot="${slot}" aria-expanded="false"${disabled ? ' disabled' : ''}>
          <div class="pcmp__dropdown-menu">
            ${items}
          </div>
        </div>
      `;
    }

    renderBody() {
      const hideSame = Boolean(this.hideSame?.checked);
      const highlightDiff = Boolean(this.highlightDiff?.checked);
      const selectedCount = this.slotProducts.filter(Boolean).length;

      const rows = this.specRows
        .map((row) => {
          const values = this.slotProducts.map((item) => (item ? ProductComparison.getSpecValue(item.specs, row.key) : ''));
          const normalized = values.map((value) => ProductComparison.normalizeComparable(value)).filter(Boolean);
          const unique = [...new Set(normalized)];
          const canCompare = selectedCount > 1;
          const identical = canCompare && unique.length <= 1;
          const different = canCompare && unique.length > 1;

          if (hideSame && identical) return '';

          const rowClass = highlightDiff && different ? ' pcmp__row--different' : '';
          const cells = values
            .map((value) => {
              const empty = value === '';
              const printable = empty ? '-' : value;
              const className = empty ? 'pcmp__value pcmp__value--empty' : 'pcmp__value';
              return `<td><div class="${className}">${ProductComparison.escape(printable)}</div></td>`;
            })
            .join('');

          return `<tr class="${rowClass.trim()}">
            <th class="pcmp__sticky pcmp__label">${ProductComparison.escape(row.label)}</th>
            ${cells}
          </tr>`;
        })
        .filter(Boolean)
        .join('');

      this.body.innerHTML = rows || `<tr><td colspan="${this.maxSlots + 1}" class="pcmp__note">No parameters available for current selection.</td></tr>`;
    }

    static parseRows(raw) {
      return String(raw || '')
        .split(/\r?\n/)
        .map((line) => line.trim())
        .filter(Boolean)
        .map((line) => {
          const parts = line.split('|');
          const label = (parts[0] || '').trim();
          const key = (parts[1] || ProductComparison.slugify(label)).trim();
          return { label, key };
        });
    }

    static specMetafieldRefs(rows) {
      const refs = [];
      const seen = new Set();

      (rows || []).forEach((row) => {
        const ref = ProductComparison.parseMetafieldRef(row?.key);
        if (!ref || seen.has(ref.canonical)) return;

        ref.alias = `spec_metafield_${refs.length}`;
        refs.push(ref);
        seen.add(ref.canonical);
      });

      return refs;
    }

    static parseMetafieldRef(value) {
      const raw = String(value || '').trim();
      const dotIndex = raw.indexOf('.');
      if (dotIndex <= 0 || dotIndex === raw.length - 1) return null;

      const namespace = raw.slice(0, dotIndex).trim();
      const key = raw.slice(dotIndex + 1).trim();
      if (!namespace || !key) return null;

      return {
        namespace,
        key,
        canonical: `${namespace}.${key}`,
      };
    }

    static normalizeLimit(value) {
      const raw = Number(value || 0);
      if (Number.isFinite(raw) && raw > 0) return raw;
      return Number.POSITIVE_INFINITY;
    }

    static normalizePageSize(value) {
      const raw = Number(value || 100);
      if (!Number.isFinite(raw)) return 100;
      return Math.max(50, Math.min(250, raw));
    }

    static productFromNode(node, specMetafieldRefs = []) {
      if (!node?.handle) return null;

      const originalName = ProductComparison.metafieldValue(node.original_name);
      const specs = {
        soc: ProductComparison.metafieldValue(node.soc),
        cpu: ProductComparison.metafieldValue(node.cpu),
        npu: ProductComparison.metafieldValue(node.npu),
        gpu: ProductComparison.metafieldValue(node.gpu),
        vpu: ProductComparison.metafieldValue(node.vpu),
        ram: ProductComparison.metafieldValue(node.ram),
        storage: ProductComparison.metafieldValue(node.storage),
        os: ProductComparison.metafieldValue(node.os),
        wireless: ProductComparison.metafieldValue(node.wireless),
        ethernet: ProductComparison.metafieldValue(node.ethernet),
        video_input: ProductComparison.metafieldValue(node.video_input),
        video_output: ProductComparison.metafieldValue(node.video_output),
        usb: ProductComparison.metafieldValue(node.usb),
        pcie: ProductComparison.metafieldValue(node.pcie),
        sata: ProductComparison.metafieldValue(node.sata),
        audio: ProductComparison.metafieldValue(node.audio),
        dimension: ProductComparison.metafieldValue(node.dimension),
        power: ProductComparison.metafieldValue(node.power),
        environment: ProductComparison.metafieldValue(node.environment),
        resource_category: ProductComparison.metafieldValue(node.resource_category),
        system_on_chip: ProductComparison.metafieldValue(node.system_on_chip),
        chip_type: ProductComparison.metafieldValue(node.chip_type),
      };

      (specMetafieldRefs || []).forEach((ref) => {
        if (!ref?.alias || !ref?.canonical) return;
        specs[ref.canonical] = ProductComparison.metafieldValue(node[ref.alias]);
      });

      return {
        id: node.id || '',
        handle: node.handle || '',
        title: originalName || node.title || '',
        url: node.onlineStoreUrl || `/products/${node.handle}`,
        image: ProductComparison.productImage(node),
        chipName: ProductComparison.metafieldValue(node.chip_filter_soc) || specs.soc || '',
        chipRefs: ProductComparison.metaobjectRefs(node.chip_filter_soc || node.soc),
        full: true,
        specs,
      };
    }

    static productIndexFromNode(node) {
      if (!node?.handle) return null;

      const chipName = ProductComparison.metafieldValue(node.soc);
      const originalName = ProductComparison.metafieldValue(node.original_name);
      return {
        id: node.id || '',
        handle: node.handle || '',
        title: originalName || node.title || '',
        url: node.onlineStoreUrl || `/products/${node.handle}`,
        image: ProductComparison.productImage(node),
        chipName,
        chipRefs: ProductComparison.metaobjectRefs(node.soc),
        full: false,
        specs: {
          soc: chipName,
        },
      };
    }

    static metaobjectRefs(field) {
      const nodes = [];
      if (field?.reference) nodes.push(field.reference);
      (field?.references?.nodes || []).forEach((node) => {
        if (node) nodes.push(node);
      });

      return nodes
        .map((node) => {
          const id = ProductComparison.stringify(node?.id || '');
          const handle = ProductComparison.stringify(node?.handle || '');
          const name = ProductComparison.metaobjectText(node);
          if (!id && !handle && !name) return null;
          return { id, handle, name };
        })
        .filter(Boolean);
    }

    static productMatchesChip(product, chip) {
      const chipId = ProductComparison.normalizeComparable(chip?.id);
      const refs = Array.isArray(product?.chipRefs) ? product.chipRefs : [];

      if (chipId) {
        return refs.some((ref) => ProductComparison.normalizeComparable(ref?.id) === chipId);
      }

      const chipCandidates = [
        chip?.handle,
        chip?.name,
      ].map((value) => ProductComparison.normalizeComparable(value)).filter(Boolean);

      if (chipCandidates.length === 0) return false;

      return refs.some((ref) => {
        const refCandidates = [
          ref?.handle,
          ref?.name,
        ].map((value) => ProductComparison.normalizeComparable(value)).filter(Boolean);

        return refCandidates.some((value) => chipCandidates.includes(value));
      });
    }

    static productChipValue(product) {
      const firstRef = Array.isArray(product?.chipRefs) ? product.chipRefs[0] : null;
      return firstRef?.id || firstRef?.name || product?.chipName || '';
    }

    static chipsFromProducts(products) {
      const chipMap = new Map();

      (products || []).forEach((product) => {
        (product?.chipRefs || []).forEach((ref) => {
          const id = ProductComparison.stringify(ref?.id || '');
          const handle = ProductComparison.stringify(ref?.handle || '');
          const name = ProductComparison.stringify(ref?.name || handle || id);
          const key = ProductComparison.normalizeComparable(id || handle || name);
          if (!key || chipMap.has(key)) return;

          chipMap.set(key, { id, handle, name });
        });
      });

      return [...chipMap.values()].sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true }));
    }

    static metafieldValue(field) {
      const values = [];
      const raw = ProductComparison.metafieldRawText(field);
      if (raw) values.push(raw);

      const referenceValue = ProductComparison.metaobjectText(field?.reference);
      if (referenceValue) values.push(referenceValue);

      const referenceNodes = field?.references?.nodes || [];
      referenceNodes.forEach((node) => {
        const value = ProductComparison.metaobjectText(node);
        if (value) values.push(value);
      });

      return ProductComparison.uniqueDisplayValues(values).join(' ');
    }

    static metafieldRawText(field) {
      const raw = String(field?.value || '').trim();
      if (!raw) return '';
      if (/^gid:\/\/shopify\//.test(raw)) return '';

      try {
        const parsed = JSON.parse(raw);
        if (field?.type === 'rich_text_field' || ProductComparison.isRichTextNode(parsed)) {
          return ProductComparison.richTextToText(parsed);
        }
        if (Array.isArray(parsed) && parsed.every((item) => /^gid:\/\/shopify\//.test(String(item || '')))) return '';
        return ProductComparison.stringify(parsed);
      } catch (_error) {
        if (/^gid:\/\/shopify\//.test(raw)) return '';
        return raw;
      }
    }

    static metaobjectText(node) {
      if (!node) return '';
      const fields = Array.isArray(node?.fields) ? node.fields : [];
      const fieldMap = new Map(fields.map((field) => [String(field?.key || '').trim(), String(field?.value || '').trim()]));
      const nameField = fields.find((field) => /_name$/i.test(String(field?.key || '').trim()));
      const nameValue = String(nameField?.value || '').trim();
      if (nameValue && !/^gid:\/\/shopify\//.test(nameValue)) return nameValue;
      return '';
    }

    static uniqueDisplayValues(values) {
      const seen = new Set();
      const unique = [];

      (values || []).forEach((value) => {
        const text = ProductComparison.stringify(value);
        const key = ProductComparison.normalizeComparable(text);
        if (!text || !key || seen.has(key)) return;
        seen.add(key);
        unique.push(text);
      });

      return unique;
    }

    static isRichTextNode(value) {
      return Boolean(value && typeof value === 'object' && typeof value.type === 'string' && Array.isArray(value.children));
    }

    static richTextToText(node) {
      if (!node) return '';

      if (Array.isArray(node)) {
        return node.map((item) => ProductComparison.richTextToText(item)).filter(Boolean).join('\n');
      }

      if (typeof node !== 'object') {
        return ProductComparison.stringify(node);
      }

      if (node.type === 'text') {
        return ProductComparison.stringify(node.value);
      }

      const children = Array.isArray(node.children) ? node.children : [];

      if (node.type === 'list') {
        return children
          .map((item) => {
            const text = ProductComparison.richTextToText(item);
            return text ? `- ${text}` : '';
          })
          .filter(Boolean)
          .join('\n');
      }

      if (node.type === 'list-item') {
        return children.map((item) => ProductComparison.richTextToText(item)).filter(Boolean).join('');
      }

      if (node.type === 'paragraph') {
        return children.map((item) => ProductComparison.richTextToText(item)).filter(Boolean).join('');
      }

      return children.map((item) => ProductComparison.richTextToText(item)).filter(Boolean).join('\n');
    }

    static normalizeSpecs(raw) {
      if (!raw) return {};
      if (typeof raw === 'object') return raw;
      if (typeof raw === 'string') {
        const trimmed = raw.trim();
        if (!trimmed) return {};
        try {
          return JSON.parse(trimmed);
        } catch (_error) {
          return {};
        }
      }
      return {};
    }

    static getSpecValue(specs, key) {
      if (!specs) return '';

      const rawKey = String(key || '').trim();
      if (rawKey && Object.prototype.hasOwnProperty.call(specs, rawKey)) {
        const directKey = ProductComparison.stringify(specs[rawKey]);
        if (directKey !== '') return directKey;
      }

      const direct = ProductComparison.readObjectPath(specs, key);
      if (direct !== '') return direct;

      const normalizedKey = ProductComparison.slugify(key);
      const normalized = ProductComparison.readObjectPath(specs, normalizedKey);
      if (normalized !== '') return normalized;

      if (Array.isArray(specs)) {
        const row = specs.find((item) => {
          if (!item || typeof item !== 'object') return false;
          const itemKey = item.key || item.name || item.label;
          return ProductComparison.slugify(itemKey) === normalizedKey;
        });
        if (row) return ProductComparison.stringify(row.value ?? row.display_value ?? '');
      }

      return '';
    }

    static readObjectPath(source, keyPath) {
      if (!source || typeof source !== 'object') return '';

      const path = String(keyPath || '').split('.').filter(Boolean);
      if (!path.length) return '';

      let cursor = source;
      for (let i = 0; i < path.length; i += 1) {
        const segment = path[i];
        if (!cursor || typeof cursor !== 'object' || !(segment in cursor)) return '';
        cursor = cursor[segment];
      }

      return ProductComparison.stringify(cursor);
    }

    static stringify(value) {
      if (value === null || value === undefined) return '';
      if (Array.isArray(value)) return value.map((item) => ProductComparison.stringify(item)).filter(Boolean).join(', ');
      if (typeof value === 'object') {
        if (typeof value.value === 'string' || typeof value.value === 'number') return String(value.value);
        return '';
      }
      return String(value).trim();
    }

    static normalizeComparable(value) {
      return String(value || '')
        .toLowerCase()
        .replace(/\s+/g, ' ')
        .trim();
    }

    static isSameComparable(left, right) {
      return ProductComparison.normalizeComparable(left) === ProductComparison.normalizeComparable(right);
    }

    static normalizeHandle(value) {
      return String(value || '').trim().toLowerCase();
    }

    static parseJson(raw, fallback) {
      try {
        return JSON.parse(raw);
      } catch (_error) {
        return fallback;
      }
    }

    static slugify(input) {
      return String(input || '')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '_')
        .replace(/^_+|_+$/g, '');
    }

    static escape(value) {
      return String(value ?? '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
    }

    static escapeSelector(value) {
      if (window.CSS?.escape) return window.CSS.escape(String(value ?? ''));
      return String(value ?? '').replace(/["\\]/g, '\\$&');
    }

    static sleep(ms) {
      return new Promise((resolve) => {
        window.setTimeout(resolve, ms);
      });
    }

    // 产品展示图：custom.thumbnail 优先(MetaImage/GenericFile/直链都认)，没配回退产品主图
    // ——与产品中心 assets/product-center.js 的 resolveProductImage 同一优先级，两页缩略图保持一致
    static productImage(node) {
      const ref = node?.thumbnail?.reference;
      const thumbUrl = ref?.image?.url
        || (ref && typeof ref.url === 'string' ? ref.url : '')
        || (typeof node?.thumbnail?.value === 'string' && /^https?:\/\//.test(node.thumbnail.value) ? node.thumbnail.value : '');
      return thumbUrl || node?.featuredImage?.url || '';
    }

    static productNodeFields(productSocKey, specMetafieldRefs = []) {
      const dynamicMetafields = (specMetafieldRefs || [])
        .filter((ref) => ref?.alias && ref?.namespace && ref?.key)
        .map((ref) => `${ref.alias}: metafield(namespace: "${ProductComparison.escapeGraphqlString(ref.namespace)}", key: "${ProductComparison.escapeGraphqlString(ref.key)}") { ${ProductComparison.metafieldSelection()} }`)
        .join('\n');

      return `
        id
        handle
        title
        original_name: metafield(namespace: "custom", key: "original_name") { ${ProductComparison.metafieldSelection()} }
        onlineStoreUrl
        featuredImage {
          url
          altText
          width
          height
        }
        thumbnail: metafield(namespace: "custom", key: "thumbnail") {
          value
          reference {
            ... on MediaImage {
              image {
                url
                altText
              }
            }
            ... on GenericFile {
              url
            }
          }
        }
        chip_filter_soc: metafield(namespace: "custom", key: "${ProductComparison.escapeGraphqlString(productSocKey)}") { ${ProductComparison.metafieldSelection()} }
        soc: metafield(namespace: "custom", key: "soc") { ${ProductComparison.metafieldSelection()} }
        cpu: metafield(namespace: "custom", key: "cpu") { ${ProductComparison.metafieldSelection()} }
        npu: metafield(namespace: "custom", key: "npu") { ${ProductComparison.metafieldSelection()} }
        gpu: metafield(namespace: "custom", key: "gpu") { ${ProductComparison.metafieldSelection()} }
        vpu: metafield(namespace: "custom", key: "vpu") { ${ProductComparison.metafieldSelection()} }
        ram: metafield(namespace: "custom", key: "ram") { ${ProductComparison.metafieldSelection()} }
        storage: metafield(namespace: "custom", key: "storage") { ${ProductComparison.metafieldSelection()} }
        os: metafield(namespace: "custom", key: "os") { ${ProductComparison.metafieldSelection()} }
        wireless: metafield(namespace: "custom", key: "wireless") { ${ProductComparison.metafieldSelection()} }
        ethernet: metafield(namespace: "custom", key: "ethernet") { ${ProductComparison.metafieldSelection()} }
        video_input: metafield(namespace: "custom", key: "video_input") { ${ProductComparison.metafieldSelection()} }
        video_output: metafield(namespace: "custom", key: "video_output") { ${ProductComparison.metafieldSelection()} }
        usb: metafield(namespace: "custom", key: "usb") { ${ProductComparison.metafieldSelection()} }
        pcie: metafield(namespace: "custom", key: "pcie") { ${ProductComparison.metafieldSelection()} }
        sata: metafield(namespace: "custom", key: "sata") { ${ProductComparison.metafieldSelection()} }
        audio: metafield(namespace: "custom", key: "audio") { ${ProductComparison.metafieldSelection()} }
        dimension: metafield(namespace: "custom", key: "dimension") { ${ProductComparison.metafieldSelection()} }
        power: metafield(namespace: "custom", key: "power") { ${ProductComparison.metafieldSelection()} }
        environment: metafield(namespace: "custom", key: "environment") { ${ProductComparison.metafieldSelection()} }
        resource_category: metafield(namespace: "custom", key: "resource_category") { ${ProductComparison.metafieldSelection()} }
        system_on_chip: metafield(namespace: "custom", key: "system_on_chip") { ${ProductComparison.metafieldSelection()} }
        chip_type: metafield(namespace: "custom", key: "chip_type") { ${ProductComparison.metafieldSelection()} }
        ${dynamicMetafields}
      `;
    }

    static metafieldSelection() {
      return `
        type
        value
        reference {
          ... on Metaobject {
            id
            handle
            fields {
              key
              value
            }
          }
        }
        references(first: 10) {
          nodes {
            ... on Metaobject {
              id
              handle
              fields {
                key
                value
              }
            }
          }
        }
      `;
    }

    static productIndexQuery(productSocKey) {
      return `
        query ProductComparisonProductIndex($handle: String!, $first: Int!, $after: String) {
          collection(handle: $handle) {
            products(first: $first, after: $after) {
              edges {
                node {
                  id
                  handle
                  title
                  original_name: metafield(namespace: "custom", key: "original_name") { ${ProductComparison.metafieldSelection()} }
                  onlineStoreUrl
                  featuredImage {
                    url
                    altText
                    width
                    height
                  }
                  thumbnail: metafield(namespace: "custom", key: "thumbnail") {
                    value
                    reference {
                      ... on MediaImage {
                        image {
                          url
                          altText
                        }
                      }
                      ... on GenericFile {
                        url
                      }
                    }
                  }
                  soc: metafield(namespace: "custom", key: "${ProductComparison.escapeGraphqlString(productSocKey)}") { ${ProductComparison.metafieldSelection()} }
                }
              }
              pageInfo {
                hasNextPage
                endCursor
              }
            }
          }
        }
      `;
    }

    static productByHandleQuery(productSocKey, specMetafieldRefs = []) {
      return `
        query ProductComparisonProductByHandle($handle: String!) {
          product(handle: $handle) {
            ${ProductComparison.productNodeFields(productSocKey, specMetafieldRefs)}
          }
        }
      `;
    }

    static escapeGraphqlString(value) {
      return String(value || '')
        .replace(/\\/g, '\\\\')
        .replace(/"/g, '\\"');
    }
  }

  customElements.define('product-comparison', ProductComparison);
}
